import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import { useTranslation } from 'react-i18next';
import PlansTableSection from './PlansTableSection';
import EditPlanModal from './EditPlanModal';
import { MarketplacePlanItem } from '../../../types/marketplacePlans';
import {
  useGetMarketplacePlansQuery,
  useUpdateFreeMarketplacePlanMutation,
} from '../../../store/services/marketplacePlansService';

export default function SubscriptionsPlansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading } = useGetMarketplacePlansQuery();
  const [updateFreeMarketplacePlan, { isLoading: isSavingPlan }] =
    useUpdateFreeMarketplacePlanMutation();

  const [selectedPlan, setSelectedPlan] = useState<MarketplacePlanItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEdit = (record: MarketplacePlanItem) => {
    if (record.free || record.code === 'FREE') {
      setSelectedPlan(record);
      setIsEditModalOpen(true);
    } else {
      navigate(`/subscriptions-plans/${record.id}/edit`);
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedPlan(null);
  };

  const handleSavePlan = async ({
    id,
    limit,
    informationsEn,
    informationsAr,
  }: {
    id: string;
    limit: number;
    informationsEn: string[];
    informationsAr: string[];
  }) => {
    if (!selectedPlan) return;
    try {
      await updateFreeMarketplacePlan({
        id,
        body: {
          productLimit: limit,
          informationsEn,
          informationsAr,
        },
      }).unwrap();

      message.success(t('home.plan-updated-success'));
      handleCloseModal();
    } catch {
      message.error(t('home.plan-update-failed'));
    }
  };

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="subscriptions-plans-page-container"
    >
      {/* Page Header */}
      <div
        className="flex flex-col gap-1"
        data-testid="subscriptions-plans-header"
      >
        <SectionTitle text={t('home.plans-subscribe-title')} />
        <SectionDesc text={t('home.plans-subscribe-desc')} />
      </div>

      {/* Plans Table Section */}
      <PlansTableSection
        plans={data?.payload || []}
        loading={isLoading}
        onEdit={handleEdit}
      />

      {/* Edit Plan Modal (Only for Free plan) */}
      <EditPlanModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        plan={selectedPlan}
        silverLimit={
          data?.payload?.find(
            (p) => p.code === 'SILVER' || p.planNameEn?.toLowerCase().includes('silver')
          )?.productLimit
        }
        isSaving={isSavingPlan}
        onSave={handleSavePlan}
      />
    </div>
  );
}
